import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3, Users, FileText, DollarSign, Download, TrendingUp, CheckCircle, XCircle,
  Plus, Trash2, Edit2, Save, Tag, Columns, Settings, UserCheck, MessageCircle, Send,
  ChevronRight, Shield, Eye, Clock, Search, RefreshCw, Globe, Wifi, Monitor,
  AlertTriangle, Activity, ArrowUpRight, ArrowDownRight, Filter, MoreHorizontal,
  Copy, ExternalLink, Ban, Unlock, ChevronDown, X, Sparkles, Zap, Database,
  CreditCard, PieChart as PieChartIcon, Mail, MapPin, Calendar, Hash, Server,
  Lock, ShieldCheck, UserPlus, Layers, Package, LayoutDashboard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { adminAPI, appealsAPI, plansAPI, suggestionsAPI, platformFieldsAPI, settingsAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTheme } from '../App';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

/* ═══════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════ */
const STATUS_CONFIG = {
  pending_payment: { label: 'Pending Payment', color: '#F59E0B', icon: Clock, dark: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', light: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
  submitted: { label: 'Submitted', color: '#3B82F6', icon: FileText, dark: 'bg-blue-500/10 text-blue-400 border-blue-500/20', light: 'bg-blue-50 text-blue-600 border-blue-200' },
  in_review: { label: 'In Review', color: '#8B5CF6', icon: Eye, dark: 'bg-purple-500/10 text-purple-400 border-purple-500/20', light: 'bg-purple-50 text-purple-600 border-purple-200' },
  approved: { label: 'Approved', color: '#10B981', icon: CheckCircle, dark: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', light: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  rejected: { label: 'Rejected', color: '#EF4444', icon: XCircle, dark: 'bg-red-500/10 text-red-400 border-red-500/20', light: 'bg-red-50 text-red-600 border-red-200' },
  completed: { label: 'Completed', color: '#06B6D4', icon: CheckCircle, dark: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20', light: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
};

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const SIDEBAR_ITEMS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'appeals', label: 'Appeals', icon: FileText },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'plans', label: 'Plans', icon: CreditCard },
  { id: 'fields', label: 'Form Fields', icon: Layers },
  { id: 'suggestions', label: 'Suggestions', icon: Tag },
  { id: 'affiliates', label: 'Affiliates', icon: UserCheck },
  { id: 'contacts', label: 'Messages', icon: MessageCircle },
  { id: 'activity', label: 'Activity Log', icon: Activity },
  { id: 'settings', label: 'Settings', icon: Settings },
];

/* ═══════════════════════════════════════════════════════
   MAIN ADMIN DASHBOARD
   ═══════════════════════════════════════════════════════ */
export default function AdminDashboard() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme !== 'aurora';

  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState(null);
  const [appeals, setAppeals] = useState([]);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [platformFields, setPlatformFields] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [sysSettings, setSysSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearch, setUserSearch] = useState('');

  // Editors
  const [editingPlan, setEditingPlan] = useState(null);
  const [newPlan, setNewPlan] = useState(null);
  const [expandedAppeal, setExpandedAppeal] = useState(null);
  const [sendingAuthority, setSendingAuthority] = useState({});

  // Styles
  const card = isDark ? 'bg-[#0A0A0C]/80 backdrop-blur-2xl border border-white/[0.06]' : 'bg-white border border-gray-200 shadow-sm';
  const cardHover = isDark ? 'hover:border-white/[0.12] hover:bg-white/[0.02]' : 'hover:border-gray-300 hover:shadow-md';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/60' : 'text-gray-500';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';
  const inputCl = isDark ? 'bg-white/[0.03] border-white/[0.08] text-white focus:border-blue-500/40' : 'bg-gray-50 border-gray-200 text-gray-900';
  const dropCl = isDark ? 'bg-[#0A0A0C] border-white/10' : 'bg-white border-gray-200';
  const itemCl = isDark ? 'text-white hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50';
  const borderCl = isDark ? 'border-white/[0.05]' : 'border-gray-100';
  const sidebarBg = isDark ? 'bg-[#060608]/90 backdrop-blur-2xl border-white/[0.06]' : 'bg-white/95 backdrop-blur-xl border-gray-200';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, appealsRes, plansRes, usersRes, suggestionsRes, fieldsRes] = await Promise.all([
        adminAPI.stats(), appealsAPI.list({ limit: 100 }), plansAPI.list(false),
        adminAPI.users({ limit: 100 }), suggestionsAPI.list(false), platformFieldsAPI.list()
      ]);
      setStats(statsRes.data);
      setAppeals(appealsRes.data.appeals || []);
      setPlans(plansRes.data || []);
      setUsers(usersRes.data.users || []);
      setSuggestions(suggestionsRes.data || []);
      setPlatformFields(fieldsRes.data || []);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadContacts = async () => {
    try { const res = await settingsAPI.getContactMessages({}); setContacts(res.data || []); } catch { try { const res = await settingsAPI.contacts(); setContacts(res.data || []); } catch {} }
  };
  const loadAffiliates = async () => {
    try { const res = await settingsAPI.affiliates(); setAffiliates(res.data.affiliates || []); setAffiliateStats(res.data); } catch {}
  };
  const loadSettings = async () => {
    try { const res = await settingsAPI.get(); setSysSettings(res.data); } catch {}
  };
  const loadActivityLog = async () => {
    try { const res = await adminAPI.activityLog({ limit: 100 }); setActivityLog(res.data || []); } catch {}
  };

  // Handlers
  const handleStatusChange = async (id, s) => { try { await appealsAPI.updateStatus(id, s); toast.success('Status updated'); loadData(); } catch { toast.error('Failed'); } };
  const handleDeleteAppeal = async (id) => { if (!window.confirm('Delete this appeal?')) return; try { await appealsAPI.delete(id); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); } };
  const handleSendToAuthority = async (appealId) => {
    setSendingAuthority(prev => ({ ...prev, [appealId]: true }));
    try { await adminAPI.sendToAuthority(appealId); toast.success('Sent to authority'); loadData(); }
    catch { toast.error('Failed to send'); }
    finally { setSendingAuthority(prev => ({ ...prev, [appealId]: false })); }
  };
  const handleExport = async (format) => {
    try {
      const res = await adminAPI.exportAppeals(format);
      if (format === 'excel' || format === 'csv') {
        const blob = new Blob([res.data], { type: format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `appeals.${format === 'excel' ? 'xlsx' : 'csv'}`; a.click();
      }
      toast.success('Export complete');
    } catch { toast.error('Export failed'); }
  };
  const handleSavePlan = async (plan) => { try { plan.id ? await plansAPI.update(plan.id, plan) : await plansAPI.create(plan); toast.success('Plan saved'); setEditingPlan(null); setNewPlan(null); loadData(); } catch { toast.error('Failed'); } };
  const handleDeletePlan = async (id) => { if (!window.confirm('Delete this plan?')) return; try { await plansAPI.delete(id); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); } };
  const handleDeleteContact = async (id) => { try { await settingsAPI.deleteContactMessage(id); toast.success('Deleted'); loadContacts(); } catch { try { await settingsAPI.deleteContact(id); toast.success('Deleted'); loadContacts(); } catch { toast.error('Failed'); } } };
  const handlePayout = async (userId) => { try { await settingsAPI.payout(userId); toast.success('Payout marked'); loadAffiliates(); } catch { toast.error('Failed'); } };
  const handleSaveSettings = async () => { if (!sysSettings) return; try { await settingsAPI.update(sysSettings); toast.success('Settings saved'); } catch { toast.error('Failed'); } };

  const filteredAppeals = appeals.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (a.tracking_code?.toLowerCase().includes(q) || a.full_name?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q) || a.platform?.toLowerCase().includes(q));
    }
    return true;
  });

  const filteredUsers = users.filter(u => {
    if (!userSearch) return true;
    const q = userSearch.toLowerCase();
    return (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
  });

  const getStatus = (s) => STATUS_CONFIG[s]?.[isDark ? 'dark' : 'light'] || '';

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  /* ═══════════════════════════════════════
     STAT CARDS DATA
     ═══════════════════════════════════════ */
  const statCards = stats ? [
    { icon: FileText, label: 'Total Appeals', value: stats.total_appeals, color: '#3B82F6', trend: '+12%' },
    { icon: DollarSign, label: 'Revenue', value: `€${stats.total_revenue?.toFixed(2) || '0.00'}`, color: '#10B981', trend: '+8%' },
    { icon: Users, label: 'Users', value: stats.total_users, color: '#8B5CF6', trend: '+5%' },
    { icon: TrendingUp, label: 'Subscribers', value: stats.total_subscribers, color: '#06B6D4', trend: '+15%' },
    { icon: CheckCircle, label: 'Approved', value: stats.approved, color: '#10B981' },
    { icon: Clock, label: 'Pending', value: stats.pending, color: '#F59E0B' },
    { icon: Eye, label: 'In Review', value: stats.in_review, color: '#8B5CF6' },
    { icon: XCircle, label: 'Rejected', value: stats.rejected, color: '#EF4444' },
  ] : [];

  return (
    <div className={`min-h-screen transition-colors ${isDark ? 'bg-[#020204]' : 'bg-[#F8FAFC]'}`} data-testid="admin-dashboard">
      {/* Mobile sidebar toggle */}
      <button onClick={() => setSidebarOpen(!sidebarOpen)} className={`fixed top-20 left-4 z-50 md:hidden w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-[#0A0A0C] border border-white/[0.08] text-white' : 'bg-white border border-gray-200 text-gray-700 shadow-sm'}`}>
        {sidebarOpen ? <X className="w-5 h-5" /> : <LayoutDashboard className="w-5 h-5" />}
      </button>

      <div className="flex">
        {/* ═══════════ SIDEBAR ═══════════ */}
        <aside className={`fixed md:sticky top-16 left-0 h-[calc(100vh-4rem)] w-64 border-r z-40 transition-transform md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${sidebarBg}`}>
          <div className="p-4 h-full overflow-y-auto custom-scrollbar">
            <div className="mb-6 px-2">
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/15 border border-blue-500/25' : 'bg-blue-50 border border-blue-200'}`}
                  style={{ boxShadow: isDark ? '0 0 20px rgba(59,130,246,0.1)' : '' }}>
                  <Shield className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h2 className={`text-sm font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>Admin Panel</h2>
                  <p className={`text-[10px] ${textM}`}>myAct Management</p>
                </div>
              </div>
            </div>

            <nav className="space-y-1">
              {SIDEBAR_ITEMS.map(item => {
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveSection(item.id);
                      setSidebarOpen(false);
                      if (item.id === 'contacts') loadContacts();
                      if (item.id === 'affiliates') loadAffiliates();
                      if (item.id === 'settings') loadSettings();
                      if (item.id === 'activity') loadActivityLog();
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? isDark ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-blue-50 text-blue-600 border border-blue-200'
                        : isDark ? 'text-white/50 hover:text-white/80 hover:bg-white/[0.03]' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                    style={isActive && isDark ? { boxShadow: '0 0 15px rgba(59,130,246,0.06)' } : {}}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    {item.label}
                    {item.id === 'appeals' && stats && (
                      <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                        {stats.total_appeals}
                      </span>
                    )}
                    {item.id === 'users' && stats && (
                      <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
                        {stats.total_users}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Quick Actions */}
            <div className={`mt-6 p-3 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/[0.04]' : 'bg-gray-50 border-gray-100'}`}>
              <p className={`text-[10px] uppercase tracking-wider mb-2 ${textM}`}>Quick Export</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleExport('csv')} className={`flex-1 h-8 text-xs rounded-lg ${isDark ? 'border-white/[0.08] text-white/50' : ''}`}>
                  <Download className="w-3 h-3 mr-1" /> CSV
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleExport('excel')} className={`flex-1 h-8 text-xs rounded-lg ${isDark ? 'border-white/[0.08] text-white/50' : ''}`}>
                  <Download className="w-3 h-3 mr-1" /> Excel
                </Button>
              </div>
            </div>
          </div>
        </aside>

        {/* ═══════════ MAIN CONTENT ═══════════ */}
        <main className="flex-1 min-h-[calc(100vh-4rem)] p-6 md:p-8 md:ml-0">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
              <div>
                <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight ${textP}`} style={{ fontFamily: 'Outfit' }}>
                  {SIDEBAR_ITEMS.find(i => i.id === activeSection)?.label || 'Dashboard'}
                </h1>
                <p className={`text-sm mt-1 ${textM}`}>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
              <Button onClick={loadData} variant="outline" size="sm" className={`rounded-xl ${isDark ? 'border-white/[0.08] text-white/50' : ''}`}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </motion.div>

            <AnimatePresence mode="wait">
              {/* ═══════════════════════════════════════
                 OVERVIEW SECTION
                 ═══════════════════════════════════════ */}
              {activeSection === 'overview' && (
                <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  {/* Stat Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {statCards.map((c, i) => (
                      <motion.div key={c.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                        className={`${card} ${cardHover} rounded-2xl p-5 transition-all cursor-default`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${c.color}15` }}>
                            <c.icon className="w-5 h-5" style={{ color: c.color }} />
                          </div>
                          {c.trend && (
                            <span className="flex items-center gap-0.5 text-xs font-medium text-emerald-500">
                              <ArrowUpRight className="w-3 h-3" />{c.trend}
                            </span>
                          )}
                        </div>
                        <div className={`text-2xl font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>{c.value}</div>
                        <div className={`text-xs mt-1 ${textM}`}>{c.label}</div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Charts */}
                  {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div className={`${card} rounded-2xl p-6`}>
                        <div className="flex items-center gap-2 mb-4">
                          <BarChart3 className="w-4 h-4 text-blue-500" />
                          <h3 className={`text-sm font-semibold ${textS}`}>Platform Distribution</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={stats.platform_distribution || []}>
                            <XAxis dataKey="platform" tick={{ fill: isDark ? '#64748B' : '#6B7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: isDark ? '#64748B' : '#6B7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ backgroundColor: isDark ? '#0A0A14' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`, borderRadius: 12, color: isDark ? '#fff' : '#111', fontSize: 12 }} />
                            <Bar dataKey="count" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className={`${card} rounded-2xl p-6`}>
                        <div className="flex items-center gap-2 mb-4">
                          <PieChartIcon className="w-4 h-4 text-purple-500" />
                          <h3 className={`text-sm font-semibold ${textS}`}>Status Overview</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Approved', value: stats.approved || 0 },
                                { name: 'Rejected', value: stats.rejected || 0 },
                                { name: 'In Review', value: stats.in_review || 0 },
                                { name: 'Pending', value: stats.pending || 0 },
                              ].filter(d => d.value > 0)}
                              cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                              {[0,1,2,3].map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                            </Pie>
                            <Tooltip contentStyle={{ backgroundColor: isDark ? '#0A0A14' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`, borderRadius: 12, color: isDark ? '#fff' : '#111', fontSize: 12 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Recent Appeals Quick View */}
                  <div className={`${card} rounded-2xl`}>
                    <div className={`p-5 border-b ${borderCl} flex items-center justify-between`}>
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-blue-500" />
                        <h3 className={`text-sm font-semibold ${textP}`}>Recent Appeals</h3>
                      </div>
                      <button onClick={() => setActiveSection('appeals')} className="text-xs text-blue-500 hover:text-blue-400 flex items-center gap-1">
                        View All <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="divide-y divide-white/[0.04]">
                      {appeals.slice(0, 5).map(a => (
                        <div key={a.id} className={`px-5 py-3 flex items-center justify-between ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'} transition-colors`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{a.tracking_code}</span>
                            <span className={`text-sm truncate ${textP}`}>{a.full_name}</span>
                            <span className={`text-xs ${textM}`}>{a.platform}</span>
                          </div>
                          <Badge className={`text-[10px] ${getStatus(a.status)}`}>{a.status?.replace('_', ' ')}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ═══════════════════════════════════════
                 APPEALS SECTION
                 ═══════════════════════════════════════ */}
              {activeSection === 'appeals' && (
                <motion.div key="appeals" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  {/* Filters */}
                  <div className={`${card} rounded-2xl p-4 mb-6`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="relative flex-1 min-w-[200px]">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textM}`} />
                        <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`pl-10 h-10 ${inputCl} rounded-xl`} placeholder="Search by name, email, tracking code..." />
                      </div>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className={`w-44 h-10 ${inputCl} rounded-xl`}>
                          <Filter className="w-3.5 h-3.5 mr-2" /><SelectValue />
                        </SelectTrigger>
                        <SelectContent className={dropCl}>
                          <SelectItem value="all" className={itemCl}>All Status</SelectItem>
                          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                            <SelectItem key={k} value={k} className={itemCl}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleExport('csv')} className={`h-10 rounded-xl ${isDark ? 'border-white/[0.08] text-white/50' : ''}`}>
                          <Download className="w-4 h-4 mr-1" /> CSV
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleExport('excel')} className={`h-10 rounded-xl ${isDark ? 'border-white/[0.08] text-white/50' : ''}`}>
                          <Download className="w-4 h-4 mr-1" /> Excel
                        </Button>
                      </div>
                    </div>
                    <p className={`text-xs mt-2 ${textM}`}>{filteredAppeals.length} appeals found</p>
                  </div>

                  {/* Appeals List */}
                  <div className="space-y-3">
                    {filteredAppeals.map((a, i) => (
                      <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                        className={`${card} rounded-2xl overflow-hidden transition-all ${expandedAppeal === a.id ? '' : cardHover}`}>
                        {/* Appeal Row */}
                        <div className={`p-5 cursor-pointer`} onClick={() => setExpandedAppeal(expandedAppeal === a.id ? null : a.id)}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <button onClick={e => { e.stopPropagation(); copyToClipboard(a.tracking_code); }} className={`text-xs font-mono px-2 py-0.5 rounded-md ${isDark ? 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'} transition-colors`}>
                                  {a.tracking_code} <Copy className="w-2.5 h-2.5 inline ml-1" />
                                </button>
                                <Badge className={`text-[10px] ${getStatus(a.status)}`}>{a.status?.replace('_', ' ')}</Badge>
                                <span className={`text-xs px-2 py-0.5 rounded-md ${a.payment_status === 'paid' ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600') : (isDark ? 'bg-yellow-500/10 text-yellow-400' : 'bg-yellow-50 text-yellow-600')}`}>
                                  {a.payment_status}
                                </span>
                                {a.sent_to_authority && (
                                  <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                    <ShieldCheck className="w-3 h-3" /> Authority
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-4">
                                <span className={`text-sm font-semibold ${textP}`}>{a.full_name}</span>
                                <span className={`text-xs ${textS}`}>{a.email}</span>
                              </div>
                              <div className={`flex flex-wrap items-center gap-3 mt-1.5 text-xs ${textM}`}>
                                <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{a.platform}</span>
                                <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{a.issue_type}</span>
                                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{a.country || 'N/A'}</span>
                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(a.created_at).toLocaleDateString()}</span>
                                {a.client_ip && <span className="flex items-center gap-1"><Wifi className="w-3 h-3" />{a.client_ip}</span>}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                              <Select value={a.status} onValueChange={(v) => handleStatusChange(a.id, v)}>
                                <SelectTrigger className={`h-8 text-xs w-36 rounded-lg border ${getStatus(a.status)}`}><SelectValue /></SelectTrigger>
                                <SelectContent className={dropCl}>
                                  {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                                    <SelectItem key={k} value={k} className={`${itemCl} text-xs`}>{v.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {!a.sent_to_authority && (
                                <Button size="sm" onClick={() => handleSendToAuthority(a.id)} disabled={sendingAuthority[a.id]}
                                  className={`h-8 text-xs rounded-lg ${isDark ? 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200'}`}>
                                  <Send className="w-3 h-3 mr-1" /> Authority
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteAppeal(a.id)} className="text-red-500 hover:text-red-400 h-8 w-8 p-0">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Expanded Detail */}
                        <AnimatePresence>
                          {expandedAppeal === a.id && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                              <div className={`px-5 pb-5 pt-2 border-t ${borderCl}`}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <p className={`text-xs uppercase tracking-wider mb-2 ${textM}`}>Description</p>
                                    <p className={`text-sm leading-relaxed ${textS}`}>{a.description || 'No description'}</p>
                                  </div>
                                  <div className="space-y-3">
                                    {a.client_ip && (
                                      <div className={`flex items-center gap-2 text-xs ${textS}`}>
                                        <Wifi className="w-3.5 h-3.5 text-blue-500" />
                                        <span className={textM}>IP:</span>
                                        <span className="font-mono">{a.client_ip}</span>
                                      </div>
                                    )}
                                    {a.user_agent && (
                                      <div className={`flex items-start gap-2 text-xs ${textS}`}>
                                        <Monitor className="w-3.5 h-3.5 text-purple-500 flex-shrink-0 mt-0.5" />
                                        <span className="truncate font-mono">{a.user_agent}</span>
                                      </div>
                                    )}
                                    {a.custom_fields && Object.keys(a.custom_fields).length > 0 && (
                                      <div>
                                        <p className={`text-xs uppercase tracking-wider mb-1 ${textM}`}>Custom Fields</p>
                                        {Object.entries(a.custom_fields).map(([k, v]) => (
                                          <div key={k} className={`text-xs ${textS}`}>
                                            <span className={textM}>{k}:</span> {v}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {a.sent_to_authority && a.sent_at && (
                                      <div className={`flex items-center gap-2 text-xs`}>
                                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                                        <span className="text-emerald-500">Sent to Authority: {new Date(a.sent_at).toLocaleString()}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                    {filteredAppeals.length === 0 && (
                      <div className={`${card} rounded-2xl p-16 text-center`}>
                        <FileText className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/10' : 'text-gray-200'}`} />
                        <p className={textM}>No appeals found</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ═══════════════════════════════════════
                 USERS SECTION
                 ═══════════════════════════════════════ */}
              {activeSection === 'users' && (
                <motion.div key="users" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <div className={`${card} rounded-2xl p-4 mb-6`}>
                    <div className="relative">
                      <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textM}`} />
                      <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} className={`pl-10 h-10 ${inputCl} rounded-xl`} placeholder="Search users..." />
                    </div>
                  </div>
                  <div className={`${card} rounded-2xl overflow-hidden`}>
                    <table className="w-full">
                      <thead>
                        <tr className={`border-b ${borderCl}`}>
                          {['User', 'Email', 'Role', 'Joined', 'Actions'].map(h => (
                            <th key={h} className={`text-left text-xs font-semibold p-4 ${textM}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map(u => (
                          <tr key={u.id} className={`border-b ${borderCl} ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'} transition-colors`}>
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                  {u.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <span className={`text-sm font-medium ${textP}`}>{u.name}</span>
                              </div>
                            </td>
                            <td className={`p-4 text-sm ${textS}`}>{u.email}</td>
                            <td className="p-4">
                              <Select value={u.role} onValueChange={async (v) => { try { await adminAPI.updateRole(u.id, v); toast.success('Role updated'); loadData(); } catch { toast.error('Failed'); } }}>
                                <SelectTrigger className={`h-8 text-xs w-36 rounded-lg ${inputCl}`}><SelectValue /></SelectTrigger>
                                <SelectContent className={dropCl}>
                                  {['subscriber','authority','super_admin','affiliate'].map(r => (
                                    <SelectItem key={r} value={r} className={`${itemCl} text-xs`}>{r.replace('_', ' ')}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className={`p-4 text-xs ${textM}`}>{new Date(u.created_at).toLocaleDateString()}</td>
                            <td className="p-4">
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={async () => { try { await adminAPI.blockUser(u.id); toast.success('User blocked'); loadData(); } catch { toast.error('Failed'); } }}
                                  className={`h-7 w-7 p-0 ${isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'}`}>
                                  <Ban className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={async () => { if (!window.confirm('Delete this user?')) return; try { await adminAPI.deleteUser(u.id); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); } }}
                                  className="text-red-500 hover:text-red-400 h-7 w-7 p-0">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredUsers.length === 0 && <div className={`p-12 text-center ${textM}`}>No users found</div>}
                  </div>
                </motion.div>
              )}

              {/* ═══════════════════════════════════════
                 PLANS SECTION
                 ═══════════════════════════════════════ */}
              {activeSection === 'plans' && (
                <motion.div key="plans" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <div className="flex justify-end mb-6">
                    <Button onClick={() => setNewPlan({ name: '', description: '', price: 0, currency: 'eur', billing_cycle: 'monthly', max_appeals: 1, features: [], active: true, sort_order: 0 })}
                      className="bg-blue-500 hover:bg-blue-600 rounded-xl text-white">
                      <Plus className="w-4 h-4 mr-2" /> Add Plan
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {plans.map((plan, i) => (
                      <motion.div key={plan.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                        className={`${card} rounded-2xl p-6 ${cardHover} transition-all`}>
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className={`font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>{plan.name}</h3>
                              <Badge className={plan.active ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600') : (isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600')}>
                                {plan.active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            <p className={`text-sm mt-1 ${textS}`}>{plan.description}</p>
                          </div>
                        </div>
                        <div className="mb-4">
                          <span className={`text-3xl font-extrabold ${textP}`}>{plan.currency === 'eur' ? '€' : '$'}{plan.price}</span>
                          <span className={`text-sm ${textM}`}> / {plan.billing_cycle}</span>
                        </div>
                        <div className={`text-xs mb-4 ${textM}`}>Max Appeals: {plan.max_appeals === -1 ? 'Unlimited' : plan.max_appeals}</div>
                        {plan.features?.length > 0 && (
                          <ul className="space-y-1.5 mb-4">
                            {plan.features.map((f, fi) => (
                              <li key={fi} className={`text-xs flex items-center gap-2 ${textS}`}>
                                <CheckCircle className="w-3 h-3 text-emerald-500" />{f}
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="flex gap-2 pt-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingPlan({...plan})} className={`flex-1 rounded-lg text-xs ${isDark ? 'border-white/[0.08] text-white/60' : ''}`}>
                            <Edit2 className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeletePlan(plan.id)} className="text-red-500 h-8 w-8 p-0">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  {(editingPlan || newPlan) && <PlanEditor plan={editingPlan || newPlan} onSave={handleSavePlan} onCancel={() => { setEditingPlan(null); setNewPlan(null); }} isDark={isDark} inputCl={inputCl} dropCl={dropCl} itemCl={itemCl} textP={textP} textM={textM} />}
                </motion.div>
              )}

              {/* ═══════════════════════════════════════
                 FORM FIELDS SECTION
                 ═══════════════════════════════════════ */}
              {activeSection === 'fields' && (
                <motion.div key="fields" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <FieldsManager fields={platformFields} onReload={loadData} isDark={isDark} card={card} textP={textP} textS={textS} textM={textM} inputCl={inputCl} dropCl={dropCl} itemCl={itemCl} borderCl={borderCl} />
                </motion.div>
              )}

              {/* ═══════════════════════════════════════
                 SUGGESTIONS SECTION
                 ═══════════════════════════════════════ */}
              {activeSection === 'suggestions' && (
                <motion.div key="suggestions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <SuggestionsManager suggestions={suggestions} onReload={loadData} isDark={isDark} card={card} textP={textP} textS={textS} textM={textM} inputCl={inputCl} />
                </motion.div>
              )}

              {/* ═══════════════════════════════════════
                 AFFILIATES SECTION
                 ═══════════════════════════════════════ */}
              {activeSection === 'affiliates' && (
                <motion.div key="affiliates" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  {affiliateStats && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      {[
                        { label: 'Commission Rate', value: `$${affiliateStats.commission_per_appeal}/appeal`, icon: DollarSign, color: '#3B82F6' },
                        { label: 'Total Pending', value: `$${(affiliateStats.total_pending || 0).toFixed(2)}`, icon: Clock, color: '#F59E0B' },
                        { label: 'Total Paid', value: `$${(affiliateStats.total_paid || 0).toFixed(2)}`, icon: CheckCircle, color: '#10B981' },
                      ].map((s, i) => (
                        <div key={i} className={`${card} rounded-2xl p-5`}>
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `${s.color}15` }}>
                            <s.icon className="w-4 h-4" style={{ color: s.color }} />
                          </div>
                          <div className={`text-xl font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>{s.value}</div>
                          <div className={`text-xs ${textM}`}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={`${card} rounded-2xl overflow-hidden`}>
                    <table className="w-full">
                      <thead><tr className={`border-b ${borderCl}`}>
                        {['Name','Email','Ref Code','Referred','Paid Appeals','Pending','Paid Out','Action'].map(h => (
                          <th key={h} className={`text-left text-xs font-semibold p-4 ${textM}`}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {affiliates.length === 0 ? (
                          <tr><td colSpan={8} className={`p-12 text-center ${textM}`}>No affiliates yet</td></tr>
                        ) : affiliates.map(a => (
                          <tr key={a.id} className={`border-b ${borderCl} ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'} transition-colors`}>
                            <td className={`p-4 text-sm font-medium ${textP}`}>{a.name}</td>
                            <td className={`p-4 text-sm ${textS}`}>{a.email}</td>
                            <td className="p-4"><span className={`text-xs font-mono px-2 py-1 rounded-md ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>{a.referral_code}</span></td>
                            <td className={`p-4 text-sm ${textP}`}>{a.referred_users}</td>
                            <td className={`p-4 text-sm ${textP}`}>{a.referred_appeals}</td>
                            <td className="p-4 text-sm font-medium text-amber-500">${a.pending_earnings?.toFixed(2)}</td>
                            <td className="p-4 text-sm font-medium text-emerald-500">${a.total_paid?.toFixed(2)}</td>
                            <td className="p-4">
                              <Button size="sm" onClick={() => handlePayout(a.id)} disabled={!a.pending_earnings || a.pending_earnings <= 0}
                                className={`rounded-lg text-xs h-8 ${a.pending_earnings > 0 ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : isDark ? 'bg-white/5 text-white/30' : 'bg-gray-100 text-gray-400'}`}>
                                Mark Paid
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}

              {/* ═══════════════════════════════════════
                 CONTACTS/MESSAGES SECTION
                 ═══════════════════════════════════════ */}
              {activeSection === 'contacts' && (
                <motion.div key="contacts" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <div className="space-y-3">
                    {contacts.length === 0 ? (
                      <div className={`${card} rounded-2xl p-16 text-center`}>
                        <MessageCircle className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/10' : 'text-gray-200'}`} />
                        <p className={textM}>No messages yet</p>
                      </div>
                    ) : contacts.map((c, i) => (
                      <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                        className={`${card} rounded-2xl p-5 ${cardHover} transition-all`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${isDark ? 'bg-purple-500/10 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
                                {c.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <span className={`font-semibold text-sm ${textP}`}>{c.name}</span>
                              <span className={`text-xs ${textM}`}>{c.email}</span>
                              <span className={`text-xs ${textM}`}>{new Date(c.created_at).toLocaleString()}</span>
                            </div>
                            <div className={`text-xs font-semibold mb-1.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{c.subject}</div>
                            <p className={`text-sm leading-relaxed ${textS}`}>{c.message}</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(c.id)} className="text-red-500 hover:text-red-400 h-8 w-8 p-0 flex-shrink-0">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ═══════════════════════════════════════
                 ACTIVITY LOG SECTION
                 ═══════════════════════════════════════ */}
              {activeSection === 'activity' && (
                <motion.div key="activity" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <div className={`${card} rounded-2xl overflow-hidden`}>
                    <div className={`p-5 border-b ${borderCl} flex items-center gap-2`}>
                      <Activity className="w-4 h-4 text-blue-500" />
                      <h3 className={`text-sm font-semibold ${textP}`}>System Activity Log</h3>
                      <span className={`text-xs ml-auto ${textM}`}>{activityLog.length} entries</span>
                    </div>
                    <div className="divide-y divide-white/[0.03] max-h-[600px] overflow-y-auto custom-scrollbar">
                      {activityLog.length === 0 ? (
                        <div className={`p-12 text-center ${textM}`}>No activity recorded</div>
                      ) : activityLog.map((log, i) => (
                        <div key={log.id || i} className={`px-5 py-3 ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'} transition-colors`}>
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                log.action === 'login' ? (isDark ? 'bg-blue-500/10' : 'bg-blue-50') :
                                log.action === 'appeal_created' ? (isDark ? 'bg-emerald-500/10' : 'bg-emerald-50') :
                                log.action === 'sent_to_authority' ? (isDark ? 'bg-purple-500/10' : 'bg-purple-50') :
                                (isDark ? 'bg-white/[0.05]' : 'bg-gray-50')
                              }`}>
                                {log.action === 'login' && <Lock className="w-3.5 h-3.5 text-blue-500" />}
                                {log.action === 'appeal_created' && <FileText className="w-3.5 h-3.5 text-emerald-500" />}
                                {log.action === 'sent_to_authority' && <Send className="w-3.5 h-3.5 text-purple-500" />}
                                {log.action === 'appeal_status_changed' && <RefreshCw className="w-3.5 h-3.5 text-amber-500" />}
                                {!['login','appeal_created','sent_to_authority','appeal_status_changed'].includes(log.action) && <Activity className="w-3.5 h-3.5 text-gray-400" />}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm font-medium ${textP}`}>{log.action?.replace(/_/g, ' ')}</span>
                                  {log.email && <span className={`text-xs ${textM}`}>{log.email}</span>}
                                </div>
                                <div className={`flex items-center gap-3 text-xs ${textM}`}>
                                  {log.ip && <span className="flex items-center gap-1 font-mono"><Wifi className="w-3 h-3" />{log.ip}</span>}
                                  {log.appeal_id && <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{log.appeal_id.slice(0, 8)}</span>}
                                </div>
                              </div>
                            </div>
                            <span className={`text-xs flex-shrink-0 ${textM}`}>{new Date(log.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ═══════════════════════════════════════
                 SETTINGS SECTION
                 ═══════════════════════════════════════ */}
              {activeSection === 'settings' && (
                <motion.div key="settings" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <div className={`${card} rounded-2xl p-6`}>
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-2">
                        <Settings className="w-5 h-5 text-blue-500" />
                        <h3 className={`text-lg font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>System Settings</h3>
                      </div>
                      <Button onClick={handleSaveSettings} className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl" style={{ boxShadow: '0 0 20px rgba(59,130,246,0.15)' }}>
                        <Save className="w-4 h-4 mr-2" /> Save Settings
                      </Button>
                    </div>
                    {sysSettings ? (
                      <div className="space-y-8">
                        {/* General */}
                        <div>
                          <h4 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${textS}`}><Globe className="w-4 h-4" /> General</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                              { key: 'site_name', label: 'Site Name', type: 'text' },
                              { key: 'support_email', label: 'Support Email', type: 'email' },
                              { key: 'arbitration_email', label: 'Arbitration Email', type: 'email' },
                              { key: 'max_free_appeals', label: 'Max Free Appeals', type: 'number' },
                            ].map(f => (
                              <div key={f.key}>
                                <Label className={`text-xs block mb-1.5 ${textM}`}>{f.label}</Label>
                                <Input type={f.type} value={sysSettings[f.key] ?? ''} onChange={e => setSysSettings({ ...sysSettings, [f.key]: f.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value })} className={`${inputCl} rounded-xl h-10`} />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Affiliate */}
                        <div>
                          <h4 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${textS}`}><UserCheck className="w-4 h-4" /> Affiliate Program</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label className={`text-xs block mb-1.5 ${textM}`}>Commission per Appeal ($)</Label>
                              <Input type="number" step="0.01" value={sysSettings.affiliate_commission ?? 0} onChange={e => setSysSettings({ ...sysSettings, affiliate_commission: parseFloat(e.target.value) || 0 })} className={`${inputCl} rounded-xl h-10`} />
                            </div>
                            <div>
                              <Label className={`text-xs block mb-1.5 ${textM}`}>Min Payout ($)</Label>
                              <Input type="number" step="0.01" value={sysSettings.affiliate_min_payout ?? 0} onChange={e => setSysSettings({ ...sysSettings, affiliate_min_payout: parseFloat(e.target.value) || 0 })} className={`${inputCl} rounded-xl h-10`} />
                            </div>
                            <div>
                              <Label className={`text-xs block mb-1.5 ${textM}`}>Program Status</Label>
                              <div className="flex items-center gap-3 h-10">
                                <button onClick={() => setSysSettings({ ...sysSettings, affiliate_active: !sysSettings.affiliate_active })}
                                  className={`relative w-12 h-6 rounded-full transition-colors ${sysSettings.affiliate_active ? 'bg-blue-500' : isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${sysSettings.affiliate_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                </button>
                                <span className={`text-sm ${sysSettings.affiliate_active ? 'text-blue-500' : textM}`}>{sysSettings.affiliate_active ? 'Active' : 'Disabled'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={`p-12 text-center ${textM}`}>Loading settings...</div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   PLAN EDITOR COMPONENT
   ═══════════════════════════════════════════════════════ */
function PlanEditor({ plan, onSave, onCancel, isDark, inputCl, dropCl, itemCl, textP, textM }) {
  const [form, setForm] = useState(plan);
  const [featureInput, setFeatureInput] = useState('');
  const addFeature = () => { if (featureInput.trim()) { setForm({ ...form, features: [...(form.features || []), featureInput.trim()] }); setFeatureInput(''); } };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`mt-6 p-6 rounded-2xl border-2 ${isDark ? 'bg-[#0A0A0C]/80 border-blue-500/20' : 'bg-white border-blue-200 shadow-xl'}`}
      style={{ boxShadow: isDark ? '0 0 40px rgba(59,130,246,0.06)' : '' }}>
      <h4 className={`font-bold mb-4 ${textP}`} style={{ fontFamily: 'Outfit' }}>{plan.id ? 'Edit Plan' : 'New Plan'}</h4>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <div><Label className={`text-xs block mb-1 ${textM}`}>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={`${inputCl} rounded-xl h-10`} /></div>
        <div><Label className={`text-xs block mb-1 ${textM}`}>Name (AR)</Label><Input value={form.name_ar || ''} onChange={e => setForm({...form, name_ar: e.target.value})} className={`${inputCl} rounded-xl h-10`} /></div>
        <div><Label className={`text-xs block mb-1 ${textM}`}>Price</Label><Input type="number" step="0.01" value={form.price} onChange={e => setForm({...form, price: parseFloat(e.target.value) || 0})} className={`${inputCl} rounded-xl h-10`} /></div>
        <div><Label className={`text-xs block mb-1 ${textM}`}>Currency</Label>
          <Select value={form.currency} onValueChange={v => setForm({...form, currency: v})}><SelectTrigger className={`${inputCl} rounded-xl h-10`}><SelectValue /></SelectTrigger><SelectContent className={dropCl}><SelectItem value="eur" className={itemCl}>EUR</SelectItem><SelectItem value="usd" className={itemCl}>USD</SelectItem></SelectContent></Select>
        </div>
        <div><Label className={`text-xs block mb-1 ${textM}`}>Billing</Label>
          <Select value={form.billing_cycle} onValueChange={v => setForm({...form, billing_cycle: v})}><SelectTrigger className={`${inputCl} rounded-xl h-10`}><SelectValue /></SelectTrigger><SelectContent className={dropCl}><SelectItem value="one_time" className={itemCl}>One-time</SelectItem><SelectItem value="monthly" className={itemCl}>Monthly</SelectItem><SelectItem value="yearly" className={itemCl}>Yearly</SelectItem></SelectContent></Select>
        </div>
        <div><Label className={`text-xs block mb-1 ${textM}`}>Max Appeals</Label><Input type="number" value={form.max_appeals} onChange={e => setForm({...form, max_appeals: parseInt(e.target.value) || 0})} className={`${inputCl} rounded-xl h-10`} /></div>
      </div>
      <div className="mb-4"><Label className={`text-xs block mb-1 ${textM}`}>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className={`${inputCl} rounded-xl h-10`} /></div>
      <div className="mb-4"><Label className={`text-xs block mb-1 ${textM}`}>Features</Label>
        <div className="flex gap-2 mb-2"><Input value={featureInput} onChange={e => setFeatureInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addFeature()} className={`${inputCl} rounded-xl h-10`} placeholder="Add feature..." /><Button onClick={addFeature} className="bg-blue-500 hover:bg-blue-600 rounded-xl text-white h-10 px-4"><Plus className="w-4 h-4" /></Button></div>
        <div className="flex flex-wrap gap-2">{(form.features || []).map((f, i) => (<span key={i} className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 ${isDark ? 'bg-white/[0.05] border border-white/[0.08] text-white/60' : 'bg-blue-50 border border-blue-100 text-blue-600'}`}><CheckCircle className="w-3 h-3" />{f}<button onClick={() => setForm({...form, features: form.features.filter((_, idx) => idx !== i)})} className="text-red-500 ml-1 hover:text-red-400">&times;</button></span>))}</div>
      </div>
      <div className="flex gap-3">
        <Button onClick={() => onSave(form)} className="bg-blue-500 hover:bg-blue-600 rounded-xl text-white"><Save className="w-4 h-4 mr-2" /> Save Plan</Button>
        <Button variant="outline" onClick={onCancel} className={`rounded-xl ${isDark ? 'border-white/[0.08] text-white/60' : ''}`}>Cancel</Button>
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════
   SUGGESTIONS MANAGER
   ═══════════════════════════════════════════════════════ */
function SuggestionsManager({ suggestions, onReload, isDark, card, textP, textS, textM, inputCl }) {
  const [newSug, setNewSug] = useState({ text: '', text_ar: '', text_de: '', platform: '', active: true, sort_order: 0 });
  const [editing, setEditing] = useState(null);

  const handleCreate = async () => { if (!newSug.text) return; try { await suggestionsAPI.create(newSug); toast.success('Created'); setNewSug({ text: '', text_ar: '', text_de: '', platform: '', active: true, sort_order: 0 }); onReload(); } catch { toast.error('Failed'); } };
  const handleUpdate = async (id, data) => { try { await suggestionsAPI.update(id, data); toast.success('Updated'); setEditing(null); onReload(); } catch { toast.error('Failed'); } };
  const handleDelete = async (id) => { try { await suggestionsAPI.delete(id); toast.success('Deleted'); onReload(); } catch { toast.error('Failed'); } };

  return (
    <div>
      <div className={`${card} rounded-2xl p-5 mb-4`}>
        <p className={`text-xs uppercase tracking-wider mb-3 ${textM}`}>Add New Suggestion</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <Input value={newSug.text} onChange={e => setNewSug({...newSug, text: e.target.value})} className={`${inputCl} rounded-xl h-10`} placeholder="English text" />
          <Input value={newSug.text_ar} onChange={e => setNewSug({...newSug, text_ar: e.target.value})} className={`${inputCl} rounded-xl h-10`} placeholder="Arabic text" />
          <Input value={newSug.text_de} onChange={e => setNewSug({...newSug, text_de: e.target.value})} className={`${inputCl} rounded-xl h-10`} placeholder="German text" />
        </div>
        <Button onClick={handleCreate} className="bg-blue-500 hover:bg-blue-600 rounded-xl text-white"><Plus className="w-4 h-4 mr-2" /> Add</Button>
      </div>
      <div className="space-y-2">
        {suggestions.map(s => (
          <div key={s.id} className={`${card} rounded-xl p-4 flex items-center justify-between`}>
            {editing === s.id ? (
              <SuggestionEditRow s={s} isDark={isDark} inputCl={inputCl} onSave={(data) => handleUpdate(s.id, data)} onCancel={() => setEditing(null)} />
            ) : (
              <>
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-medium ${textP}`}>{s.text}</span>
                  {s.text_ar && <span className={`text-xs ml-2 ${textS}`}>| {s.text_ar}</span>}
                  {s.text_de && <span className={`text-xs ml-2 ${textS}`}>| {s.text_de}</span>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(s.id)} className="text-blue-500 h-8 w-8 p-0"><Edit2 className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-red-500 h-8 w-8 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SuggestionEditRow({ s, isDark, inputCl, onSave, onCancel }) {
  const [form, setForm] = useState({ text: s.text, text_ar: s.text_ar || '', text_de: s.text_de || '' });
  return (
    <div className="flex items-center gap-2 w-full">
      <Input value={form.text} onChange={e => setForm({...form, text: e.target.value})} className={`${inputCl} rounded-lg h-9 flex-1`} />
      <Input value={form.text_ar} onChange={e => setForm({...form, text_ar: e.target.value})} className={`${inputCl} rounded-lg h-9 w-28`} placeholder="AR" />
      <Input value={form.text_de} onChange={e => setForm({...form, text_de: e.target.value})} className={`${inputCl} rounded-lg h-9 w-28`} placeholder="DE" />
      <Button size="sm" onClick={() => onSave(form)} className="bg-blue-500 text-white rounded-lg h-9 px-3"><Save className="w-3 h-3" /></Button>
      <Button size="sm" variant="ghost" onClick={onCancel} className="h-9 px-2 text-red-500"><X className="w-3 h-3" /></Button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   FIELDS MANAGER
   ═══════════════════════════════════════════════════════ */
function FieldsManager({ fields, onReload, isDark, card, textP, textS, textM, inputCl, dropCl, itemCl, borderCl }) {
  const [platform, setPlatform] = useState('');
  const [newField, setNewField] = useState({ platform: '', field_name: '', field_label: '', field_label_ar: '', field_label_de: '', field_type: 'text', required: false, options: [], sort_order: 0 });
  const [optInput, setOptInput] = useState('');

  const filteredFields = platform ? fields.filter(f => f.platform === platform) : fields;
  const platforms = [...new Set(fields.map(f => f.platform))].filter(Boolean);

  const handleCreate = async () => {
    if (!newField.platform || !newField.field_name || !newField.field_label) return;
    try { await platformFieldsAPI.create(newField); toast.success('Field created'); setNewField({ platform: '', field_name: '', field_label: '', field_label_ar: '', field_label_de: '', field_type: 'text', required: false, options: [], sort_order: 0 }); onReload(); } catch { toast.error('Failed'); }
  };
  const handleDelete = async (id) => { try { await platformFieldsAPI.delete(id); toast.success('Deleted'); onReload(); } catch { toast.error('Failed'); } };

  return (
    <div>
      <div className={`${card} rounded-2xl p-5 mb-6`}>
        <p className={`text-xs uppercase tracking-wider mb-3 ${textM}`}>Add New Field</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          <Input value={newField.platform} onChange={e => setNewField({...newField, platform: e.target.value})} className={`${inputCl} rounded-xl h-10`} placeholder="Platform (e.g. Facebook)" />
          <Input value={newField.field_name} onChange={e => setNewField({...newField, field_name: e.target.value})} className={`${inputCl} rounded-xl h-10`} placeholder="field_name" />
          <Input value={newField.field_label} onChange={e => setNewField({...newField, field_label: e.target.value})} className={`${inputCl} rounded-xl h-10`} placeholder="Label (EN)" />
          <Input value={newField.field_label_ar} onChange={e => setNewField({...newField, field_label_ar: e.target.value})} className={`${inputCl} rounded-xl h-10`} placeholder="Label (AR)" />
          <Select value={newField.field_type} onValueChange={v => setNewField({...newField, field_type: v})}>
            <SelectTrigger className={`${inputCl} rounded-xl h-10`}><SelectValue /></SelectTrigger>
            <SelectContent className={dropCl}>
              {['text','textarea','select','email','url','number'].map(t => (<SelectItem key={t} value={t} className={itemCl}>{t}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={newField.required} onChange={e => setNewField({...newField, required: e.target.checked})} className="rounded" />
            <label className={`text-xs ${textS}`}>Required</label>
          </div>
        </div>
        {newField.field_type === 'select' && (
          <div className="mb-3">
            <div className="flex gap-2 mb-2">
              <Input value={optInput} onChange={e => setOptInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && optInput.trim()) { setNewField({...newField, options: [...newField.options, optInput.trim()]}); setOptInput(''); }}} className={`${inputCl} rounded-xl h-9`} placeholder="Add option..." />
            </div>
            <div className="flex flex-wrap gap-1">{newField.options.map((o, i) => (<span key={i} className={`text-xs px-2 py-1 rounded-lg ${isDark ? 'bg-white/5 text-white/60' : 'bg-blue-50 text-blue-600'}`}>{o} <button onClick={() => setNewField({...newField, options: newField.options.filter((_,idx) => idx !== i)})} className="text-red-500 ml-1">&times;</button></span>))}</div>
          </div>
        )}
        <Button onClick={handleCreate} className="bg-blue-500 hover:bg-blue-600 rounded-xl text-white"><Plus className="w-4 h-4 mr-2" /> Add Field</Button>
      </div>

      {platforms.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap">
          <Button variant={!platform ? 'default' : 'outline'} size="sm" onClick={() => setPlatform('')} className={`rounded-xl ${!platform ? 'bg-blue-500 text-white' : isDark ? 'border-white/[0.08] text-white/60' : ''}`}>All</Button>
          {platforms.map(p => (
            <Button key={p} variant={platform === p ? 'default' : 'outline'} size="sm" onClick={() => setPlatform(p)} className={`rounded-xl ${platform === p ? 'bg-blue-500 text-white' : isDark ? 'border-white/[0.08] text-white/60' : ''}`}>{p}</Button>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {filteredFields.map(f => (
          <div key={f.id} className={`${card} rounded-xl p-4 flex items-center justify-between`}>
            <div>
              <div className="flex items-center gap-2">
                <Badge className={isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}>{f.platform}</Badge>
                <span className={`text-sm font-medium ${textP}`}>{f.field_label}</span>
                <span className={`text-xs ${textM}`}>({f.field_type})</span>
                {f.required && <Badge className={isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'}>Required</Badge>}
              </div>
              <p className={`text-xs mt-1 ${textM}`}>Field: {f.field_name}{f.field_label_ar ? ` | AR: ${f.field_label_ar}` : ''}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => handleDelete(f.id)} className="text-red-500 h-8 w-8 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
        {filteredFields.length === 0 && <p className={`text-center py-8 ${textM}`}>No custom fields defined yet</p>}
      </div>
    </div>
  );
}
